import { sumBy } from '@markku/utils'
import { z } from 'zod'

export interface ServiceTaskLine {
  taskId: number
  /** Computed price for this booking, in euros. */
  price: number
  /**
   * Appended to the catalogue name when the line is snapshotted onto the visit
   * (`visit_tasks.task_name`), e.g. " (3 kohdetta, 5 tulisijaa)". Set when one
   * line stands for several units of work, so the row on the visit and on the
   * invoice states the real scope instead of the catalogue's default wording.
   */
  taskNameSuffix?: string
}

export interface ServiceBookingData {
  tasks: ServiceTaskLine[]
  serviceDetails: string
  estimatedDurationMinutes: number
  estimatedCost: number
  /**
   * One line the worker must see before leaving for the job (e.g. bring
   * scaffolding). Goes to the top of the visit's internal notes, ahead of the
   * booking summary, so it survives the day view's truncated preview.
   */
  workerNotice?: string
}

/**
 * `inferFormValue`'s answer for a visit this preset booked but cannot read back.
 * Distinct from null, which means the lines aren't this preset's at all: the
 * search through earlier visits stops here, so a taloyhtiö swept as twelve sites
 * last autumn isn't offered the single-fireplace price of the visit before it.
 */
export const UNREADABLE_SERVICE_CONFIG = 'unreadable'

/** A task line as it was actually booked, read back off a visit. */
export interface BookedTaskLine {
  maintenanceTaskId: number | null
  /** Gross line price in euros. Null when the line was never priced. */
  price: number | null
}

/**
 * Every preset key, derived from the schema registry at the bottom of this file
 * so adding a preset means editing one map instead of a hand-kept union.
 */
export type ServicePresetKey = keyof typeof PRESET_FORM_VALUE_SCHEMAS

/** One bookable arrival window, in Helsinki wall-clock hours. */
export interface BookingWindowDefinition {
  key: string
  label: string
  startHour: number
  endHour: number
}

/**
 * Window ("slot") booking: the customer books an arrival window instead of a
 * time, and the route planner fixes the actual time later. Only presets with
 * this config offer windows; the rest keep exact-time booking.
 */
export interface WindowBookingConfig {
  /** Windows offered per day. One 8–16 entry turns this into whole-day booking. */
  windows: BookingWindowDefinition[]
  /** Gross surcharge for choosing an exact time at a window edge. */
  exactTimeSurchargeEur: number
  /**
   * Planning estimate for the drive to a stop booked into a window. Replaced
   * by real travel times once the planner has run; until then it is what
   * keeps a window from being sold with no room to drive between stops.
   */
  travelAllowanceMinutes: number
  /**
   * Helsinki hour on the day before the visit at which still-flexible times
   * are fixed and sent to customers. Window bookings close at that moment;
   * exact-time bookings stay open (minBookingAdvanceHours).
   */
  lockDeadlineHour: number
}

/**
 * Name of the billable line the exact-time surcharge is recorded under.
 * Shared so the backend insert and the ERP's staff-created line can't drift.
 */
export const EXACT_TIME_LINE_NAME = 'Tarkka kellonaika'

/** The per-employee pace fields the duration override reads. */
export interface EmployeePace {
  nuohousFirstFireplaceMinutes: number | null
  nuohousExtraFireplaceMinutes: number | null
}

export interface ServicePresetData {
  id: string
  name: string
  description: string
  maxDrivingMinutes: number
  /**
   * Optional low-season fallback. When a strict search (using maxDrivingMinutes)
   * returns no slots, retry the near term with a looser cap — those slots have
   * low opportunity cost (they'd likely go unfilled anyway), so a slightly longer
   * drive beats offering no slot at all. Each tier is one extra search: `daysAhead`
   * bounds the window to now → end of that day (so today is included, not just
   * tomorrow), and `extraMinutes` is added to the base cap. Windows overlap, so a
   * day's effective cap is the largest bonus among tiers whose window covers it —
   * meaning `extraMinutes` MUST be non-increasing as `daysAhead` grows (sooner =
   * more relaxed), which is also the intent. Only set for tightly-capped services.
   */
  drivingRelaxation?: { daysAhead: number; extraMinutes: number }[]
  minBookingAdvanceHours: number
  /**
   * The grid exact times start on. 5 where jobs are short and a sweeper does
   * many a day: the first time after a stop then starts when the drive allows
   * instead of waiting for the next quarter hour, and tight gaps get found.
   * 15 elsewhere: hours-long jobs gain nothing from it, and nobody expects a
   * crew at 12:35 sharp.
   */
  slotStepMinutes: number
  slotSpacingMinutes: number
  /**
   * People who want a copy of each new booking of this preset, on top of the
   * back-office address (BOOKING_NOTIFICATION_EMAIL) and the assigned employee.
   * Never the shared info@ inbox — its AI processing would just file the
   * notification away unread.
   */
  notificationEmails: string[]
  defaultFormValue: Record<string, unknown>
  getBookingData: (formValue: Record<string, unknown>) => ServiceBookingData
  /**
   * Inverse of `getBookingData`: recover the answers from a visit's booked task
   * lines, so re-booking a property can start from what it had last time.
   * Returns null when the lines don't belong to this preset or the answers
   * can't be recovered — the caller then falls back to `defaultFormValue`.
   *
   * Presets whose answers collapse irreversibly into one priced line (both IV
   * calculators) simply omit this rather than guess.
   */
  inferFormValue?: (
    tasks: BookedTaskLine[],
  ) => Record<string, unknown> | typeof UNREADABLE_SERVICE_CONFIG | null
  /**
   * Screening answers that put the job outside online pricing (e.g. a house too
   * big to quote from a size band). The widget routes these to a quote request,
   * but that is a client-side decision — the public booking and time-slot routes
   * enforce this too, so a hand-crafted payload can't book a job we never priced.
   */
  needsQuote?: (formValue: Record<string, unknown>) => boolean
  /**
   * Answers only staff can book. The form values are validated by one schema
   * shared with the unauthenticated widget routes, so a staff-only answer has
   * to be refused at the route rather than by the schema — otherwise a crafted
   * payload books a job whose duration blocks weeks of an employee's calendar.
   *
   * Unlike `needsQuote` this is NOT enforced on the slot search: the ERP asks
   * that same public route for times, and refusing there would leave staff with
   * no slots to pick for the very bookings this allows.
   */
  staffOnly?: (formValue: Record<string, unknown>) => boolean
  windowBooking?: WindowBookingConfig
  /**
   * Duration for a specific employee, when the preset lets pace vary per
   * person. Absent means `getBookingData(...).estimatedDurationMinutes` holds
   * for everyone.
   */
  getEmployeeDurationMinutes?: (
    formValue: Record<string, unknown>,
    employee: EmployeePace,
  ) => number
}

// --- Nuohous (chimney sweep) preset data ---

interface NuohousOption {
  fireplaces: number
  taskId: number
  duration: number
  cost: number
}

interface NuohousBoilerOption {
  taskId: number
  duration: number
  cost: number
}

/** Upper bound offered by the staff form's dropdowns. */
export const NUOHOUS_MAX_COUNT = 50

export const nuohousFormValueSchema = z.object({
  /**
   * Buildings / apartments swept on this visit. Optional so the public widget —
   * which never asks — prices exactly as before: absent means a single site.
   */
  buildingCount: z.number().int().min(1).max(NUOHOUS_MAX_COUNT).optional(),
  /** Fireplaces across the whole visit, not per building. */
  fireplaces: z.number().int().min(1).max(NUOHOUS_MAX_COUNT),
  /** The public widget's yes/no answer. Staff send `boilerCount` instead. */
  hasBoiler: z.boolean().optional(),
  boilerCount: z.number().int().min(0).max(NUOHOUS_MAX_COUNT).optional(),
})

export type NuohousFormValue = z.infer<typeof nuohousFormValueSchema>

/** Boilers on the visit, from whichever of the two answers the client sent. */
export function resolveNuohousBoilerCount({
  hasBoiler,
  boilerCount,
}: NuohousFormValue): number {
  return boilerCount ?? (hasBoiler ? 1 : 0)
}

// Fireplace options: fireplaces, taskId, duration (min), cost (€).
// Task IDs match production — the same IDs are seeded locally via fixtures.
export const NUOHOUS_OPTIONS: NuohousOption[] = [
  { fireplaces: 1, taskId: 1741, duration: 25, cost: 68 },
  { fireplaces: 2, taskId: 54356, duration: 40, cost: 88 },
  { fireplaces: 3, taskId: 54373, duration: 55, cost: 108 },
  { fireplaces: 4, taskId: 54323, duration: 70, cost: 128 },
  { fireplaces: 5, taskId: 54238, duration: 85, cost: 148 },
]

// Boiler sweep add-on: taskId, duration (min), cost (€)
export const NUOHOUS_BOILER_OPTION: NuohousBoilerOption = {
  taskId: 54240,
  duration: 15,
  cost: 25,
}

// A booking that covers several sites, or more fireplaces than the largest
// bundle, gets its own catalogue task rather than a bundle row with a
// multiplied price: profitability reporting reads the task NAME off the
// catalogue by ID (`modules/profitability/router.ts`), so a 3-site job billed
// under "Nuohous - 2 tulisijaa" would report a fireplace count that never
// happened. The task's name must still start with "Nuohous" — the employee
// app finds the sweeping certificate's task that way, and the service-trend
// chart groups on the part before " - ".
//
// Matches production; the same ID is seeded locally via fixtures.
export const NUOHOUS_MULTI_SITE_TASK_ID = 2216199

// The bundle table is exactly base + step × (fireplaces − 1): the first
// fireplace at a site carries the visit's setup, each further one is cheaper.
export const NUOHOUS_BASE_COST = NUOHOUS_OPTIONS[0].cost
export const NUOHOUS_EXTRA_FIREPLACE_COST = 20
export const NUOHOUS_BASE_DURATION = NUOHOUS_OPTIONS[0].duration
export const NUOHOUS_EXTRA_FIREPLACE_DURATION = 15

/**
 * Largest count the bundle tasks name. Past it a booking has no catalogue task
 * of its own, so it goes on the multi-site task and can no longer be read back
 * off a visit.
 */
export const NUOHOUS_MAX_FIREPLACES = Math.max(
  ...NUOHOUS_OPTIONS.map((o) => o.fireplaces),
)

/**
 * How the two counts split into priced units. `fireplaces` is the visit total
 * across every building, so each building's first fireplace is charged at the
 * base rate — that is what makes three one-fireplace apartments 3 × 68 € rather
 * than one 108 € three-fireplace house. More buildings than fireplaces just
 * means some have nothing to sweep, so only the ones that do are billed.
 */
export function splitNuohousUnits({
  buildingCount,
  fireplaces,
}: {
  buildingCount: number
  fireplaces: number
}) {
  const sites = Math.min(buildingCount, fireplaces)
  return { sites, extraFireplaces: fireplaces - sites }
}

/** Total sweep price for the whole visit, in euros. */
export function getNuohousCost(counts: {
  buildingCount: number
  fireplaces: number
}): number {
  const { sites, extraFireplaces } = splitNuohousUnits(counts)
  return (
    NUOHOUS_BASE_COST * sites + NUOHOUS_EXTRA_FIREPLACE_COST * extraFireplaces
  )
}

/** Total sweep duration for the whole visit, in minutes. */
function getNuohousDuration(
  counts: { buildingCount: number; fireplaces: number },
  pace: { firstFireplace: number; extraFireplace: number } = {
    firstFireplace: NUOHOUS_BASE_DURATION,
    extraFireplace: NUOHOUS_EXTRA_FIREPLACE_DURATION,
  },
): number {
  const { sites, extraFireplaces } = splitNuohousUnits(counts)
  return pace.firstFireplace * sites + pace.extraFireplace * extraFireplaces
}

/** Sweep duration at this employee's pace (preset defaults where unset). */
export function getNuohousEmployeeDurationMinutes(
  formValue: Record<string, unknown>,
  employee: EmployeePace,
): number {
  const value = formValue as NuohousFormValue
  return (
    getNuohousDuration(
      { buildingCount: value.buildingCount ?? 1, fireplaces: value.fireplaces },
      {
        firstFireplace:
          employee.nuohousFirstFireplaceMinutes ?? NUOHOUS_BASE_DURATION,
        extraFireplace:
          employee.nuohousExtraFireplaceMinutes ??
          NUOHOUS_EXTRA_FIREPLACE_DURATION,
      },
    ) +
    NUOHOUS_BOILER_OPTION.duration * resolveNuohousBoilerCount(value)
  )
}

/**
 * Several sites, or counts past what the widget's own form can express. The
 * public booking route refuses these; the ERP creates them directly.
 */
export function isStaffOnlyNuohousBooking(
  formValue: Record<string, unknown>,
): boolean {
  const value = formValue as NuohousFormValue
  return (
    (value.buildingCount ?? 1) > 1 ||
    value.fireplaces > NUOHOUS_MAX_FIREPLACES ||
    resolveNuohousBoilerCount(value) > 1
  )
}

export function getNuohousBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const value = formValue as NuohousFormValue
  const buildings = value.buildingCount ?? 1
  const fireplaces = value.fireplaces
  const boilers = resolveNuohousBoilerCount(value)

  const bundle = NUOHOUS_OPTIONS.find((o) => o.fireplaces === fireplaces)
  // The bundle tasks name a fireplace count, so they only describe a single
  // site within the table's range; anything else goes on the multi-site task.
  const fitsBundle = buildings === 1 && !!bundle
  const fireplaceText = `${fireplaces} tulisija${fireplaces > 1 ? 'a' : ''}`
  const siteText =
    buildings > 1 ? `${buildings} kohdetta, ${fireplaceText}` : fireplaceText

  const tasks: ServiceTaskLine[] = [
    {
      taskId: fitsBundle ? bundle.taskId : NUOHOUS_MULTI_SITE_TASK_ID,
      price: getNuohousCost({ buildingCount: buildings, fireplaces }),
      taskNameSuffix: fitsBundle ? undefined : ` (${siteText})`,
    },
  ]

  if (boilers > 0) {
    tasks.push({
      taskId: NUOHOUS_BOILER_OPTION.taskId,
      price: NUOHOUS_BOILER_OPTION.cost * boilers,
      // The boiler task's name claims no quantity, so multiplying its price
      // under the same ID misreports nothing.
      taskNameSuffix: boilers > 1 ? ` (${boilers} kpl)` : undefined,
    })
  }

  const totalCost = sumBy(tasks, (t) => t.price)
  const totalDuration =
    getNuohousDuration({ buildingCount: buildings, fireplaces }) +
    NUOHOUS_BOILER_OPTION.duration * boilers
  const boilerText = boilers
    ? ` + kattilanuohous${boilers > 1 ? ` (${boilers} kpl)` : ''}`
    : ''

  return {
    tasks,
    serviceDetails: `${siteText}${boilerText} - ${totalCost} €`,
    estimatedDurationMinutes: totalDuration,
    estimatedCost: totalCost,
  }
}

// Fireplace counts are summed across matching lines: a visit billed as two
// separate "1 tulisija" rows swept two fireplaces. Rare (≈1% of real visits)
// but unambiguous. A sum past the largest option can't be expressed as a form
// answer, so it degrades to the default rather than silently under-booking.
export function inferNuohousFormValue(
  tasks: BookedTaskLine[],
): NuohousFormValue | typeof UNREADABLE_SERVICE_CONFIG | null {
  // The multi-site task carries no count — the seller sets the price by hand —
  // so the answers can't be read back, and no older visit can stand in for
  // them either: this property is a multi-site job now.
  if (tasks.some((t) => t.maintenanceTaskId === NUOHOUS_MULTI_SITE_TASK_ID)) {
    return UNREADABLE_SERVICE_CONFIG
  }

  const fireplaces = sumBy(
    tasks,
    (t) =>
      NUOHOUS_OPTIONS.find((o) => o.taskId === t.maintenanceTaskId)
        ?.fireplaces ?? 0,
  )
  if (!fireplaces || fireplaces > NUOHOUS_MAX_FIREPLACES) return null

  // One line covers every boiler on the visit, so the count only survives in
  // its price. A line the seller repriced divides into no whole number of
  // boilers — and guessing one would quietly re-book a two-boiler property for
  // a single sweep, so the visit counts as unreadable instead.
  const boilerLine = tasks.find(
    (t) => t.maintenanceTaskId === NUOHOUS_BOILER_OPTION.taskId,
  )
  if (!boilerLine) return { fireplaces, boilerCount: 0 }

  const boilerCount = (boilerLine.price ?? 0) / NUOHOUS_BOILER_OPTION.cost
  if (!Number.isInteger(boilerCount) || boilerCount < 1) {
    return UNREADABLE_SERVICE_CONFIG
  }

  return { fireplaces, boilerCount }
}

// --- Ilmanvaihto (ventilation cleaning) preset data ---

export const apartmentSizeSchema = z.enum([
  'under_80',
  '80_to_120',
  '120_to_160',
])
export type ApartmentSize = z.infer<typeof apartmentSizeSchema>

export const floorsSchema = z.enum(['single', 'multiple'])
export type Floors = z.infer<typeof floorsSchema>

export const ventilationTypeSchema = z.enum([
  'natural',
  'mechanical_exhaust',
  'heat_recovery',
])
export type VentilationType = z.infer<typeof ventilationTypeSchema>

export const ilmanvaihtoFormValueSchema = z.object({
  apartmentSize: apartmentSizeSchema,
  floors: floorsSchema,
  ventilationType: ventilationTypeSchema,
  includeAirflowMeasurement: z.boolean().optional(),
  includeCookerHoodCleaning: z.boolean().optional(),
})

export type IlmanvaihtoFormValue = z.infer<typeof ilmanvaihtoFormValueSchema>

// Prices by size × ventilation type
export const IV_CLEANING_PRICES: Record<
  ApartmentSize,
  Record<VentilationType, number>
> = {
  under_80: { natural: 300, mechanical_exhaust: 390, heat_recovery: 450 },
  '80_to_120': {
    natural: 350,
    mechanical_exhaust: 450,
    heat_recovery: 520,
  },
  '120_to_160': {
    natural: 400,
    mechanical_exhaust: 490,
    heat_recovery: 590,
  },
}

// Airflow measurement add-on prices (50% of standalone, varies by size)
export const IV_MEASUREMENT_PRICES: Record<ApartmentSize, number> = {
  under_80: 75,
  '80_to_120': 100,
  '120_to_160': 125,
}

export const IV_MULTI_FLOOR_SURCHARGE = 50
export const IV_COOKER_HOOD_PRICE = 60

// Duration estimates (minutes) — full lookup table (from IV expert)
const IV_DURATION: Record<ApartmentSize, Record<VentilationType, number>> = {
  under_80: { natural: 150, mechanical_exhaust: 150, heat_recovery: 180 },
  '80_to_120': { natural: 180, mechanical_exhaust: 180, heat_recovery: 220 },
  '120_to_160': { natural: 180, mechanical_exhaust: 180, heat_recovery: 270 },
}

const IV_DURATION_MULTI_FLOOR = 15
const IV_DURATION_COOKER_HOOD = 30

const IV_DURATION_MEASUREMENT: Record<
  ApartmentSize,
  Record<'mechanical_exhaust' | 'heat_recovery', number>
> = {
  under_80: { mechanical_exhaust: 20, heat_recovery: 45 },
  '80_to_120': { mechanical_exhaust: 40, heat_recovery: 60 },
  '120_to_160': { mechanical_exhaust: 60, heat_recovery: 100 },
}

// Task IDs match production — the same IDs are seeded locally via fixtures.
export const IV_TASK_IDS: Record<
  VentilationType | 'measurement' | 'cookerHood',
  number
> = {
  natural: 60317,
  mechanical_exhaust: 69448,
  heat_recovery: 69042,
  measurement: 69419,
  cookerHood: 82225,
}

const IV_TYPE_LABELS: Record<VentilationType, string> = {
  natural: 'painovoimainen',
  mechanical_exhaust: 'koneellinen poisto',
  heat_recovery: 'tulo- ja poisto (LTO)',
}

const IV_SIZE_LABELS: Record<ApartmentSize, string> = {
  under_80: 'alle 80 m²',
  '80_to_120': '80–120 m²',
  '120_to_160': '120–160 m²',
}

export function getIlmanvaihtoBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const {
    apartmentSize: size,
    floors,
    ventilationType: ventType,
    includeAirflowMeasurement = false,
    includeCookerHoodCleaning: hasCookerHood = false,
  } = formValue as IlmanvaihtoFormValue
  // Measurement add-on is only valid for non-natural ventilation
  const hasMeasurement = includeAirflowMeasurement && ventType !== 'natural'

  // Main task price = base + multi-floor surcharge folded in (no separate task)
  const mainTaskPrice =
    IV_CLEANING_PRICES[size][ventType] +
    (floors === 'multiple' ? IV_MULTI_FLOOR_SURCHARGE : 0)

  const tasks: ServiceTaskLine[] = [
    { taskId: IV_TASK_IDS[ventType], price: mainTaskPrice },
  ]
  if (hasMeasurement) {
    tasks.push({
      taskId: IV_TASK_IDS.measurement,
      price: IV_MEASUREMENT_PRICES[size],
    })
  }
  if (hasCookerHood) {
    tasks.push({
      taskId: IV_TASK_IDS.cookerHood,
      price: IV_COOKER_HOOD_PRICE,
    })
  }

  // Duration
  let duration = IV_DURATION[size][ventType]
  if (floors === 'multiple') duration += IV_DURATION_MULTI_FLOOR
  if (hasMeasurement)
    duration +=
      IV_DURATION_MEASUREMENT[size][
        ventType as 'mechanical_exhaust' | 'heat_recovery'
      ]
  if (hasCookerHood) duration += IV_DURATION_COOKER_HOOD

  const totalCost = sumBy(tasks, (t) => t.price)

  // Service details string (compact, single line — used in emails and internal notes)
  const parts = [
    `IV-puhdistus, ${IV_TYPE_LABELS[ventType]}, ${IV_SIZE_LABELS[size]}`,
  ]
  if (floors === 'multiple') parts.push('useampi kerros')
  if (hasMeasurement) parts.push('mittaus ja säätö')
  if (hasCookerHood) parts.push('liesituuletin')

  return {
    tasks,
    serviceDetails: `${parts.join(', ')} - ${totalCost} €`,
    estimatedDurationMinutes: duration,
    estimatedCost: totalCost,
  }
}

// --- Ilmanvaihto v2 (granular per-room calculator on markku.fi) ---
//
// The new website calculator prices per room/feature instead of by apartment
// size band. Price and duration are computed separately: the price from the
// euro table below, the duration from its own minute table. This formula is
// the server-authoritative port of the client calculator at
// apps/markku-website .../ilmanvaihtokanavien-puhdistus — the website only
// shows a preview; these numbers are the source of truth.
//
// ⚠️ KEEP IN SYNC: the markku-website repo (separate repo) hardcodes a mirror
// copy of these constants in its IV calculator (index.astro + [city].astro,
// search "ÄLÄ MUOKKAA ILMAN TECH-TIIMIÄ") for the instant preview. If you
// change a price or the duration rule here, update that copy too — otherwise
// the website preview will diverge from the confirmed/invoiced amount.

// --- Vent height (shared by IV cleaning and measurement) ---
//
// Highest vent in the house. Under 3,5 m is the base price; 3,5–4,5 m is
// reached from a ladder for a surcharge; above 4,5 m needs scaffolding, which
// also costs time. Never a quote gate (IV production, 11.9.2026) — the height
// rides in serviceDetails and so in the visit's internal notes.

export const ventHeightSchema = z.enum(['standard', 'up_to_4_5', 'over_4_5'])
export type VentHeight = z.infer<typeof ventHeightSchema>

export const VENT_HEIGHT_SURCHARGE: Record<VentHeight, number> = {
  standard: 0,
  up_to_4_5: 60,
  over_4_5: 120,
}

const VENT_HEIGHT_MINUTES: Record<VentHeight, number> = {
  standard: 0,
  up_to_4_5: 0,
  over_4_5: 30,
}

// Customer-facing: the scaffolding behind the top band is our business and
// is spelled out only in serviceDetails / the internal notes.
export const VENT_HEIGHT_LABELS: Record<VentHeight, string> = {
  standard: 'Alle 3,5 m',
  up_to_4_5: '3,5–4,5 m',
  over_4_5: 'Yli 4,5 m',
}

/** serviceDetails fragment, or null at the base height. */
function ventHeightDetail(ventHeight: VentHeight): string | null {
  if (ventHeight === 'standard') return null
  const label = VENT_HEIGHT_LABELS[ventHeight].toLowerCase()
  return `venttiilit ${label}${ventHeight === 'over_4_5' ? ' (telineet)' : ''}`
}

/** The one line the worker must not miss: scaffolding goes in the van. */
function ventHeightWorkerNotice(ventHeight: VentHeight): string | undefined {
  return ventHeight === 'over_4_5'
    ? 'HUOM: telineet mukaan – venttiilejä yli 4,5 m korkeudella'
    : undefined
}

export const ilmanvaihtoV2FormValueSchema = z.object({
  ventilationType: ventilationTypeSchema,
  floors: z.number().int().min(1).max(5),
  // Rooms (excl. living room & kitchen) — only billed for heat_recovery (LTO)
  bedrooms: z.number().int().min(0).max(7).optional().default(0),
  walkInClosets: z.number().int().min(0).max(9).optional().default(0),
  bathrooms: z.number().int().min(1).max(9),
  sauna: z.boolean().optional().default(false),
  ventHeight: ventHeightSchema.optional().default('standard'),
  // LTO-only add-ons
  filterChange: z.boolean().optional().default(false),
  greaseDuct: z.boolean().optional().default(false),
  // Non-LTO only: supply-air valve filter count
  supplyAirFilters: z.number().int().min(0).max(9).optional().default(0),
})

export type IlmanvaihtoV2FormValue = z.infer<
  typeof ilmanvaihtoV2FormValueSchema
>

const IV_V2_LTO_BASE = 250
const IV_V2_OTHER_BASE = 280 // mechanical_exhaust & natural share the same base
const IV_V2_BEDROOM_ADD: Record<number, number> = {
  0: 0,
  1: 35,
  2: 70,
  3: 105,
  4: 140,
  5: 175,
  6: 240,
  7: 310,
}
const IV_V2_WALK_IN_CLOSET = 25
const IV_V2_BATHROOM = 25
const IV_V2_SAUNA = 50
const IV_V2_FLOOR = 40
// One price for the one catalogue task, whichever preset places it.
export const IV_FILTER_CHANGE = 50
const IV_V2_GREASE_DUCT = 50
const IV_V2_SUPPLY_AIR_FILTER = 15

// Task IDs match production. Main cleaning tasks + cooker-hood are shared with
// the v1 preset; the filter-change task (875026) exists only in prod.
export const IV_V2_FILTER_CHANGE_TASK_ID = 875026

/** The task ids the IV booking presets place. */
export const VENTILATION_TASK_IDS: number[] = [
  ...Object.values(IV_TASK_IDS),
  IV_V2_FILTER_CHANGE_TASK_ID,
]

const VENTILATION_TASK_ID_SET = new Set(VENTILATION_TASK_IDS)

/**
 * Ventilation work is the only service that needs pre-visit answers today.
 * Lives next to the ids so a new IV task can't be added to one caller's copy
 * of the list and missed by another's.
 *
 * Keep this to the preset ids: a `true` here asks the customer for the
 * questionnaire and arms the reminder nudges, so widening it sends mail.
 * The field view's sections use `isVentilationWork` below instead.
 */
export function isVentilationVisit(taskIds: number[]): boolean {
  return taskIds.some((id) => VENTILATION_TASK_ID_SET.has(id))
}

/**
 * Ventilation work however it was booked, for deciding what a worker sees on
 * the visit — a wider net than the presets, because the ERP and B2B side place
 * their own catalogue rows. Measured against the 2026-08-27 dump: these are the
 * rows that already carry work content or a measurement protocol today but fall
 * outside the presets. Adding a row here is cheap (it only offers a section);
 * adding one to VENTILATION_TASK_IDS is not (it mails the customer).
 */
export const VENTILATION_WORK_TASK_IDS: number[] = [
  ...VENTILATION_TASK_IDS,
  // Second catalogue rows for services already listed above.
  1919964, // Ilmanvaihtokanavien puhdistus - Koneellinen tulo- ja poisto (= 69042)
  2013937, // Ilmanvaihtokoneen suodattimien vaihto (= 875026)
  // IV work the presets don't book.
  106276, // IV-korjaukset
  106352, // Rasvakanavien puhdistus
  106362, // IV-kartoitus
  106368, // IV-asennus
  605546, // Korvausilmaventtiilien suodattimien vaihto
  843592, // Laakerinvaihto yhteen puhaltimeen
  843621, // Laakerinvaihto kahteen puhaltimeen
]

const VENTILATION_WORK_TASK_ID_SET = new Set(VENTILATION_WORK_TASK_IDS)

export function isVentilationWork(taskIds: number[]): boolean {
  return taskIds.some((id) => VENTILATION_WORK_TASK_ID_SET.has(id))
}

const IV_MEASUREMENT_ONLY_TASK_ID_SET = new Set([
  IV_TASK_IDS.measurement,
  IV_V2_FILTER_CHANGE_TASK_ID,
])

/**
 * A standalone measurement (iv_mittaus), possibly with its filter add-on. The
 * questionnaire is skipped for these when the property's cleaning visit has
 * already asked — the answers live on the property, not the visit.
 */
export function isIvMeasurementOnly(taskIds: number[]): boolean {
  return (
    taskIds.length > 0 &&
    taskIds.every((id) => IV_MEASUREMENT_ONLY_TASK_ID_SET.has(id))
  )
}

/**
 * Filter changes are the reason the questionnaire exists: without the unit
 * model we can't buy the filter in time, so these visits are chased harder.
 */
export function includesFilterChange(taskIds: number[]): boolean {
  return taskIds.includes(IV_V2_FILTER_CHANGE_TASK_ID)
}

// Tasks that only exist when there is a machine to service. Listed positively
// rather than as "not natural": a painovoimainen visit can carry a cooker hood
// or sweep task too, and asking for the type plate of a machine the house
// doesn't have wastes the one question that matters most.
const VENTILATION_UNIT_TASK_ID_SET = new Set([
  IV_TASK_IDS.mechanical_exhaust,
  IV_TASK_IDS.heat_recovery,
  IV_TASK_IDS.measurement,
  IV_V2_FILTER_CHANGE_TASK_ID,
])

/** Painovoimaisessa ilmanvaihdossa ei ole konetta — merkki/malli jää pois. */
export function hasVentilationUnit(taskIds: number[]): boolean {
  return taskIds.some((id) => VENTILATION_UNIT_TASK_ID_SET.has(id))
}

export function computeIlmanvaihtoV2Total(
  formValue: IlmanvaihtoV2FormValue,
): number {
  const {
    ventilationType: ventType,
    floors,
    bedrooms = 0,
    walkInClosets = 0,
    bathrooms,
    sauna = false,
    ventHeight = 'standard',
    filterChange = false,
    greaseDuct = false,
    supplyAirFilters = 0,
  } = formValue
  const isLto = ventType === 'heat_recovery'

  let total = isLto
    ? IV_V2_LTO_BASE + IV_V2_BEDROOM_ADD[bedrooms]
    : IV_V2_OTHER_BASE
  total += walkInClosets * IV_V2_WALK_IN_CLOSET
  total += Math.max(0, bathrooms - 1) * IV_V2_BATHROOM
  if (sauna) total += IV_V2_SAUNA
  total += (floors - 1) * IV_V2_FLOOR
  total += VENT_HEIGHT_SURCHARGE[ventHeight]
  if (isLto && filterChange) total += IV_FILTER_CHANGE
  if (isLto && greaseDuct) total += IV_V2_GREASE_DUCT
  if (!isLto) total += supplyAirFilters * IV_V2_SUPPLY_AIR_FILTER

  return Math.max(0, Math.round(total))
}

// Minutes per selection, given by IV production management (2.9.2026).
// Duration no longer follows the price: an add-on can be worth less time than
// it bills. Prices are unchanged — only these numbers move a booking's length.
const IV_V2_BASE_MINUTES: Record<VentilationType, number> = {
  heat_recovery: 105,
  mechanical_exhaust: 120,
  natural: 120,
}

/** Cumulative minutes for 0–7 rooms: 12 min each up to five, then 15. */
const IV_V2_ROOM_MINUTES = [0, 12, 24, 36, 48, 60, 75, 90]

const IV_V2_WALK_IN_CLOSET_MINUTES = 10
const IV_V2_BATHROOM_MINUTES = 10
const IV_V2_SAUNA_MINUTES = 13
const IV_V2_FLOOR_MINUTES = 18
const IV_V2_GREASE_DUCT_MINUTES = 15
// Fractional by design; the rounding at the end of the sum absorbs it.
const IV_V2_SUPPLY_AIR_FILTER_MINUTES = 7.2
// The filter change bills 50 € but adds no time: it happens while the ducts run.

// Duration is the sum of the selected minutes, rounded UP to the nearest 10 min.
export function computeIlmanvaihtoV2DurationMinutes(
  formValue: IlmanvaihtoV2FormValue,
): number {
  const {
    ventilationType: ventType,
    floors,
    bedrooms = 0,
    walkInClosets = 0,
    bathrooms,
    sauna = false,
    ventHeight = 'standard',
    greaseDuct = false,
    supplyAirFilters = 0,
  } = formValue
  const isLto = ventType === 'heat_recovery'

  const minutes =
    IV_V2_BASE_MINUTES[ventType] +
    (isLto ? IV_V2_ROOM_MINUTES[bedrooms] : 0) +
    walkInClosets * IV_V2_WALK_IN_CLOSET_MINUTES +
    Math.max(0, bathrooms - 1) * IV_V2_BATHROOM_MINUTES +
    (sauna ? IV_V2_SAUNA_MINUTES : 0) +
    (floors - 1) * IV_V2_FLOOR_MINUTES +
    VENT_HEIGHT_MINUTES[ventHeight] +
    (isLto && greaseDuct ? IV_V2_GREASE_DUCT_MINUTES : 0) +
    (isLto ? 0 : supplyAirFilters * IV_V2_SUPPLY_AIR_FILTER_MINUTES)

  return Math.ceil(minutes / 10) * 10
}

export function getIlmanvaihtoV2BookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const value = formValue as IlmanvaihtoV2FormValue
  const {
    ventilationType: ventType,
    floors,
    bedrooms = 0,
    walkInClosets = 0,
    bathrooms,
    sauna = false,
    ventHeight = 'standard',
    filterChange = false,
    greaseDuct = false,
    supplyAirFilters = 0,
  } = value
  const isLto = ventType === 'heat_recovery'

  const total = computeIlmanvaihtoV2Total(value)
  const hasFilterChange = isLto && filterChange
  const hasGreaseDuct = isLto && greaseDuct

  // The room/floor/sauna scaling has no dedicated task — it folds into the main
  // cleaning task. Filter change and grease duct are itemized as separate lines.
  const mainTaskPrice =
    total -
    (hasFilterChange ? IV_FILTER_CHANGE : 0) -
    (hasGreaseDuct ? IV_V2_GREASE_DUCT : 0)

  const tasks: ServiceTaskLine[] = [
    { taskId: IV_TASK_IDS[ventType], price: mainTaskPrice },
  ]
  if (hasGreaseDuct) {
    tasks.push({ taskId: IV_TASK_IDS.cookerHood, price: IV_V2_GREASE_DUCT })
  }
  if (hasFilterChange) {
    tasks.push({
      taskId: IV_V2_FILTER_CHANGE_TASK_ID,
      price: IV_FILTER_CHANGE,
    })
  }

  const parts = [`IV-puhdistus, ${IV_TYPE_LABELS[ventType]}`]
  if (floors > 1) parts.push(`${floors} kerrosta`)
  if (isLto && bedrooms > 0) parts.push(`${bedrooms} huonetta`)
  if (walkInClosets > 0) parts.push(`${walkInClosets} vaatehuonetta`)
  if (bathrooms > 1) parts.push(`${bathrooms} wc/kylpyhuonetta`)
  if (sauna) parts.push('sauna')
  const heightDetail = ventHeightDetail(ventHeight)
  if (heightDetail) parts.push(heightDetail)
  if (hasFilterChange) parts.push('IV-suodattimen vaihto')
  if (hasGreaseDuct) parts.push('rasvahormi')
  if (!isLto && supplyAirFilters > 0)
    parts.push(`${supplyAirFilters} korvausilmasuodatinta`)

  return {
    tasks,
    serviceDetails: `${parts.join(', ')} - ${total} €`,
    estimatedDurationMinutes: computeIlmanvaihtoV2DurationMinutes(value),
    estimatedCost: total,
    workerNotice: ventHeightWorkerNotice(ventHeight),
  }
}

// --- Ilmalämpöpumpun huolto ja syväpuhdistus (heat pump service) preset ---
//
// Flat per-device pricing: the customer picks how many indoor units to clean,
// which sets both the price and the calendar block length. A single
// maintenance task (ILP_HUOLTO_TASK_ID) carries the whole booking; the
// device-count band only changes the line price and duration.
//
// `hasObstacle` is the screening answer (pump in a stairwell / above 3.5 m).
// It needs special equipment, so those bookings are routed to a quote request
// in the widget and never reach this booking flow — the field is kept optional
// here so the form value still validates, but it does not affect pricing.

export const ilpHuoltoFormValueSchema = z.object({
  deviceCount: z.union([z.literal(1), z.literal(2)]),
  hasObstacle: z.boolean().optional(),
})

export type IlpHuoltoFormValue = z.infer<typeof ilpHuoltoFormValueSchema>

interface IlpHuoltoOption {
  deviceCount: number
  duration: number
  cost: number
}

// Task ID matches production — the same ID is seeded locally via fixtures.
export const ILP_HUOLTO_TASK_ID = 62267

// Device count → gross price (€, VAT incl.) and calendar block (min).
export const ILP_HUOLTO_OPTIONS: IlpHuoltoOption[] = [
  { deviceCount: 1, duration: 120, cost: 270 },
  { deviceCount: 2, duration: 180, cost: 390 },
]

export function getIlpHuoltoBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const { deviceCount, hasObstacle } = formValue as IlpHuoltoFormValue
  const opt =
    ILP_HUOLTO_OPTIONS.find((o) => o.deviceCount === deviceCount) ??
    ILP_HUOLTO_OPTIONS[0]

  const tasks: ServiceTaskLine[] = [
    { taskId: ILP_HUOLTO_TASK_ID, price: opt.cost },
  ]

  // Obstacle bookings never reach the calendar flow — they're routed to a
  // quote request, where this serviceDetails is the only signal the back
  // office gets. Spell out the obstacle so it isn't mistaken for a standard,
  // already-priced job (the device-count radio is hidden in that branch, so
  // the price below is just the default and shouldn't be acted on).
  const serviceDetails = hasObstacle
    ? 'Ilmalämpöpumpun huolto ja syväpuhdistus — pumppu portaikossa tai yli 3,5 m korkeudella, vaatii erikoiskaluston (tarjouspyyntö)'
    : `Ilmalämpöpumpun huolto ja syväpuhdistus, ${opt.deviceCount} kpl - ${opt.cost} €`

  return {
    tasks,
    serviceDetails,
    estimatedDurationMinutes: opt.duration,
    estimatedCost: opt.cost,
  }
}

// One task id carries every device count, so the band is recoverable only from
// the line price. A repriced or discounted line matches nothing and falls back
// to the default — better than assuming a device count we'd then quote on.
export function inferIlpHuoltoFormValue(
  tasks: BookedTaskLine[],
): IlpHuoltoFormValue | null {
  const line = tasks.find((t) => t.maintenanceTaskId === ILP_HUOLTO_TASK_ID)
  if (!line) return null

  const opt = ILP_HUOLTO_OPTIONS.find((o) => o.cost === line.price)
  return opt ? { deviceCount: opt.deviceCount as 1 | 2 } : null
}

// --- Sadevesikourujen puhdistus (gutter cleaning) preset ---
//
// Flat per-size-band pricing: the customer picks the house size, which sets the
// line price. A single maintenance task (SADEVESIKOURUT_TASK_ID) carries the
// whole booking.
//
// Both bookable bands reserve the same 2 h block. That's deliberate — we don't
// yet have enough completed visits to know how the bands differ in practice,
// and an over-reserved calendar is cheaper to fix than an under-reserved one.
//
// `over_250` is the screening band: houses that big aren't priced online, so
// the widget routes them to a quote request and they never reach this booking
// flow. It's part of the union so the form value still validates.

export const gutterHouseSizeSchema = z.enum([
  'under_150',
  '150_to_250',
  'over_250',
])
export type GutterHouseSize = z.infer<typeof gutterHouseSizeSchema>

export const sadevesikourutFormValueSchema = z.object({
  houseSize: gutterHouseSizeSchema,
})

export type SadevesikourutFormValue = z.infer<
  typeof sadevesikourutFormValueSchema
>

interface SadevesikourutOption {
  houseSize: GutterHouseSize
  label: string
  duration: number
  /** 0 for the quote-only band, which has no online price. */
  cost: number
}

/**
 * The band we can't price online. Every consumer (widget screening, ERP form,
 * serviceDetails) asks through here so the rule is stated once — a price of 0
 * is the consequence, not the definition.
 */
export function isGutterQuoteOnly(houseSize: GutterHouseSize): boolean {
  return houseSize === 'over_250'
}

// Task ID matches production — the same ID is seeded locally via fixtures.
// Task 1713 is named "Sadevesikourujen syyspuhdistus" and carries no default
// price; the per-line price below is authoritative for preset bookings.
export const SADEVESIKOURUT_TASK_ID = 1713

// House size → gross price (€, VAT incl.) and calendar block (min).
export const SADEVESIKOURUT_OPTIONS: SadevesikourutOption[] = [
  { houseSize: 'under_150', label: 'Alle 150 m²', duration: 120, cost: 199 },
  { houseSize: '150_to_250', label: '150–250 m²', duration: 120, cost: 249 },
  { houseSize: 'over_250', label: 'Yli 250 m²', duration: 120, cost: 0 },
]

export function getSadevesikourutBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const { houseSize } = formValue as SadevesikourutFormValue
  const opt =
    SADEVESIKOURUT_OPTIONS.find((o) => o.houseSize === houseSize) ??
    SADEVESIKOURUT_OPTIONS[0]

  const tasks: ServiceTaskLine[] = [
    { taskId: SADEVESIKOURUT_TASK_ID, price: opt.cost },
  ]

  // Over-250 bookings never reach the calendar flow — they're routed to a
  // quote request, where this serviceDetails is the only signal the back
  // office gets. Say outright that the job is unpriced so it isn't actioned
  // as a standard, already-priced booking.
  const serviceDetails = isGutterQuoteOnly(opt.houseSize)
    ? 'Sadevesikourujen puhdistus — yli 250 m², vaatii erillisen tarjouksen (tarjouspyyntö)'
    : `Sadevesikourujen puhdistus, ${opt.label.toLowerCase()} - ${opt.cost} €`

  return {
    tasks,
    serviceDetails,
    estimatedDurationMinutes: opt.duration,
    estimatedCost: opt.cost,
  }
}

// Size band recoverable only from the line price, as with ilp_huolto. The
// quote-only band is skipped: its 0 € price would match any unpriced line, and
// it can't be booked anyway.
export function inferSadevesikourutFormValue(
  tasks: BookedTaskLine[],
): SadevesikourutFormValue | null {
  const line = tasks.find((t) => t.maintenanceTaskId === SADEVESIKOURUT_TASK_ID)
  if (!line) return null

  const opt = SADEVESIKOURUT_OPTIONS.find(
    (o) => !isGutterQuoteOnly(o.houseSize) && o.cost === line.price,
  )
  return opt ? { houseSize: opt.houseSize } : null
}

// --- Ilmamäärien mittaus ja säätö (standalone airflow measurement) preset ---
//
// Prices and durations from IV production (10.9.2026). Sold as its own visit,
// not folded into a cleaning — hence its own preset instead of the v1 add-on.
// Painovoimainen ventilation has no machine to measure, so it isn't in the
// type enum at all: the schema refuses it rather than a screening answer.

export const ivMittausVentilationTypeSchema = z.enum([
  'mechanical_exhaust',
  'heat_recovery',
])
export type IvMittausVentilationType = z.infer<
  typeof ivMittausVentilationTypeSchema
>

export const ivMittausFormValueSchema = z.object({
  ventilationType: ivMittausVentilationTypeSchema,
  // Same 1–5 counter as the cleaning form; the price table only knows one
  // floor vs. more (see ivMittausFloorBand).
  floors: z.number().int().min(1).max(5),
  ventHeight: ventHeightSchema.optional().default('standard'),
  // LTO only: a measurement is only reliable on clean filters.
  filterChange: z.boolean().optional().default(false),
})

export type IvMittausFormValue = z.infer<typeof ivMittausFormValueSchema>

export const IV_MITTAUS_TASK_ID = IV_TASK_IDS.measurement

// The vent-height surcharge folds into the measurement line like the floors —
// there is no catalogue task for it.
export const IV_MITTAUS_PRICES: Record<
  IvMittausVentilationType,
  Record<Floors, number>
> = {
  mechanical_exhaust: { single: 160, multiple: 180 },
  heat_recovery: { single: 220, multiple: 240 },
}

// The filter change bills 50 € but adds no time, as in the v2 calculator.
const IV_MITTAUS_MINUTES: Record<
  IvMittausVentilationType,
  Record<Floors, number>
> = {
  mechanical_exhaust: { single: 45, multiple: 60 },
  heat_recovery: { single: 75, multiple: 90 },
}

/** The price table knows one floor vs. more; the form counts 1–5. */
export function ivMittausFloorBand(floors: number): Floors {
  return floors > 1 ? 'multiple' : 'single'
}

export function getIvMittausBookingData(
  formValue: Record<string, unknown>,
): ServiceBookingData {
  const {
    ventilationType: ventType,
    floors,
    ventHeight = 'standard',
    filterChange = false,
  } = formValue as IvMittausFormValue
  const hasFilterChange = ventType === 'heat_recovery' && filterChange
  const band = ivMittausFloorBand(floors)

  const tasks: ServiceTaskLine[] = [
    {
      taskId: IV_MITTAUS_TASK_ID,
      price: getIvMittausLinePrice({ ventType, band, ventHeight }),
    },
  ]
  if (hasFilterChange) {
    tasks.push({
      taskId: IV_V2_FILTER_CHANGE_TASK_ID,
      price: IV_FILTER_CHANGE,
    })
  }
  const totalCost = sumBy(tasks, (t) => t.price)

  const parts = [
    `Ilmamäärien mittaus ja säätö, ${IV_TYPE_LABELS[ventType]}, ${floors === 1 ? '1 kerros' : `${floors} kerrosta`}`,
  ]
  const heightDetail = ventHeightDetail(ventHeight)
  if (heightDetail) parts.push(heightDetail)
  if (hasFilterChange) parts.push('IV-suodattimen vaihto')

  return {
    tasks,
    serviceDetails: `${parts.join(', ')} - ${totalCost} €`,
    estimatedDurationMinutes:
      IV_MITTAUS_MINUTES[ventType][band] + VENT_HEIGHT_MINUTES[ventHeight],
    estimatedCost: totalCost,
    workerNotice: ventHeightWorkerNotice(ventHeight),
  }
}

function getIvMittausLinePrice({
  ventType,
  band,
  ventHeight,
}: {
  ventType: IvMittausVentilationType
  band: Floors
  ventHeight: VentHeight
}): number {
  return IV_MITTAUS_PRICES[ventType][band] + VENT_HEIGHT_SURCHARGE[ventHeight]
}

// One task id carries every band, so the answers come back from the line
// price alone. The height surcharge makes some sums collide (LTO at 220 € is
// also poisto + 3,5–4,5 m), and a wrong guess would under-reserve the
// calendar — so only a price with exactly one reading is recovered.
export function inferIvMittausFormValue(
  tasks: BookedTaskLine[],
): IvMittausFormValue | typeof UNREADABLE_SERVICE_CONFIG | null {
  const line = tasks.find((t) => t.maintenanceTaskId === IV_MITTAUS_TASK_ID)
  if (!line) return null

  // Only an LTO booking places the filter line, so its presence settles the
  // ventilation type before the price is even read.
  const filterChange = tasks.some(
    (t) => t.maintenanceTaskId === IV_V2_FILTER_CHANGE_TASK_ID,
  )
  const ventTypes = filterChange
    ? (['heat_recovery'] as const)
    : ivMittausVentilationTypeSchema.options

  // The line only tells one floor from more, so "more" comes back as 2.
  const matches = ventTypes.flatMap((ventType) =>
    floorsSchema.options.flatMap((band) =>
      ventHeightSchema.options
        .filter(
          (ventHeight) =>
            getIvMittausLinePrice({ ventType, band, ventHeight }) ===
            line.price,
        )
        .map((ventHeight) => ({
          ventilationType: ventType,
          floors: band === 'single' ? 1 : 2,
          ventHeight,
        })),
    ),
  )
  // No reading at all means the line isn't this preset's — the v1 add-on
  // priced the same task at 75/100/125 — so the search may go on to an older
  // visit. Several readings means it IS ours and we just can't tell them
  // apart: half the price table collides (220 € is LTO at base height and
  // poisto at 3,5–4,5 m alike), and letting that fall through to the default
  // would offer a 340 € two-hour job at the 160 €/45 min base.
  if (!matches.length) return null
  if (matches.length > 1) return UNREADABLE_SERVICE_CONFIG

  return { ...matches[0], filterChange }
}

/**
 * Maps each preset key to the schema its `presetFormValue` must satisfy. This
 * is the single source of truth for which presets exist: `ServicePresetKey` is
 * `keyof` this map, and the booking/time-slot discriminated unions in
 * `visit.ts` are generated from it, so neither has to be edited by hand.
 *
 * `as const satisfies` (not a `Record<...>` annotation) is load-bearing — it
 * keeps each value's precise schema type, which is what lets the generated
 * unions still narrow `presetFormValue` from `servicePresetKey`.
 *
 * Keeping this separate from SERVICE_PRESET_DATA below is safe: that map is
 * annotated `Record<ServicePresetKey, ...>`, so a key present in one and
 * missing from the other is a compile error in both directions.
 */
export const PRESET_FORM_VALUE_SCHEMAS = {
  nuohous: nuohousFormValueSchema,
  ilmanvaihto: ilmanvaihtoFormValueSchema,
  ilmanvaihto_v2: ilmanvaihtoV2FormValueSchema,
  ilp_huolto: ilpHuoltoFormValueSchema,
  sadevesikourut: sadevesikourutFormValueSchema,
  iv_mittaus: ivMittausFormValueSchema,
} as const satisfies Record<string, z.ZodTypeAny>

/** Zod enum of every preset key, derived from the registry so it can't drift. */
export const servicePresetKeySchema = z.enum(
  Object.keys(PRESET_FORM_VALUE_SCHEMAS) as [
    ServicePresetKey,
    ...ServicePresetKey[],
  ],
)

/** Registry of all service presets */
export const SERVICE_PRESET_DATA: Record<ServicePresetKey, ServicePresetData> =
  {
    nuohous: {
      id: 'nuohous',
      name: 'Nuohous',
      description: 'Hormin ja tulisijan nuohous',
      maxDrivingMinutes: 15,
      drivingRelaxation: [
        { daysAhead: 1, extraMinutes: 10 },
        { daysAhead: 2, extraMinutes: 5 },
      ],
      minBookingAdvanceHours: 4,
      slotStepMinutes: 5,
      slotSpacingMinutes: 45,
      notificationEmails: [],
      defaultFormValue: {
        fireplaces: 1,
        hasBoiler: false,
      } satisfies NuohousFormValue as Record<string, unknown>,
      getBookingData: getNuohousBookingData,
      inferFormValue: inferNuohousFormValue,
      staffOnly: isStaffOnlyNuohousBooking,
      getEmployeeDurationMinutes: getNuohousEmployeeDurationMinutes,
      windowBooking: {
        // A window is only offered where a sweeper's availability overlaps
        // it, so the evening window surfaces solely on days someone has an
        // explicit evening shift — standard 8–16 days are unaffected.
        windows: [
          { key: 'am', label: 'Aamupäivä', startHour: 8, endHour: 12 },
          { key: 'pm', label: 'Iltapäivä', startHour: 12, endHour: 16 },
          { key: 'evening', label: 'Ilta', startHour: 16, endHour: 20 },
        ],
        // 68 € + 11 € = 79 € — keeps the total under the 80 € threshold.
        exactTimeSurchargeEur: 11,
        travelAllowanceMinutes: 15,
        lockDeadlineHour: 15,
      },
    },
    ilmanvaihto: {
      id: 'ilmanvaihto',
      name: 'IV-kanavien puhdistus (vanha)',
      description: 'Ilmanvaihtokanavien puhdistus',
      maxDrivingMinutes: 60,
      minBookingAdvanceHours: 16,
      slotStepMinutes: 15,
      slotSpacingMinutes: 240,
      notificationEmails: ['jani.issakainen@markku.fi'],
      defaultFormValue: {
        apartmentSize: 'under_80',
        floors: 'single',
        ventilationType: 'natural',
        includeAirflowMeasurement: false,
        includeCookerHoodCleaning: false,
      } satisfies IlmanvaihtoFormValue as Record<string, unknown>,
      getBookingData: getIlmanvaihtoBookingData,
    },
    ilmanvaihto_v2: {
      id: 'ilmanvaihto_v2',
      name: 'IV-kanavien puhdistus',
      description: 'Ilmanvaihtokanavien puhdistus',
      maxDrivingMinutes: 60,
      minBookingAdvanceHours: 16,
      slotStepMinutes: 15,
      slotSpacingMinutes: 240,
      notificationEmails: ['jani.issakainen@markku.fi'],
      defaultFormValue: {
        ventilationType: 'heat_recovery',
        floors: 1,
        bedrooms: 0,
        walkInClosets: 0,
        bathrooms: 1,
        sauna: false,
        ventHeight: 'standard',
        filterChange: false,
        greaseDuct: false,
        supplyAirFilters: 0,
      } satisfies IlmanvaihtoV2FormValue as Record<string, unknown>,
      getBookingData: getIlmanvaihtoV2BookingData,
    },
    ilp_huolto: {
      id: 'ilp_huolto',
      name: 'Ilmalämpöpumpun huolto ja syväpuhdistus',
      description: 'Ilmalämpöpumpun huolto ja syväpuhdistus',
      maxDrivingMinutes: 60,
      minBookingAdvanceHours: 16,
      slotStepMinutes: 15,
      // Start-to-start spacing of offered slots. 150 min yields the desired
      // 8:00 / 10:30 / 13:00 cadence when the availability window starts at 8:00.
      slotSpacingMinutes: 150,
      notificationEmails: [],
      defaultFormValue: {
        deviceCount: 1,
        hasObstacle: false,
      } satisfies IlpHuoltoFormValue as Record<string, unknown>,
      getBookingData: getIlpHuoltoBookingData,
      inferFormValue: inferIlpHuoltoFormValue,
    },
    sadevesikourut: {
      id: 'sadevesikourut',
      name: 'Sadevesikourujen puhdistus',
      description: 'Sadevesikourujen eli rännien puhdistus',
      maxDrivingMinutes: 60,
      minBookingAdvanceHours: 16,
      slotStepMinutes: 15,
      // Same 2 h job shape as ilp_huolto, so the same start-to-start spacing:
      // 150 min yields an 8:00 / 10:30 / 13:00 cadence from an 8:00 window.
      slotSpacingMinutes: 150,
      notificationEmails: [],
      defaultFormValue: {
        houseSize: 'under_150',
      } satisfies SadevesikourutFormValue as Record<string, unknown>,
      getBookingData: getSadevesikourutBookingData,
      inferFormValue: inferSadevesikourutFormValue,
      // Over-250 m² has no online price, so getBookingData returns a 0 € line
      // for it. Without this gate a public caller could book that band and get
      // a real visit — and a real employee slot — for nothing.
      needsQuote: (formValue) =>
        isGutterQuoteOnly((formValue as SadevesikourutFormValue).houseSize),
    },
    iv_mittaus: {
      id: 'iv_mittaus',
      name: 'Ilmamäärien mittaus ja säätö',
      description: 'Ilmamäärien mittaus ja venttiilien säätö',
      maxDrivingMinutes: 60,
      minBookingAdvanceHours: 16,
      slotStepMinutes: 15,
      // Unlike IV cleaning (240 = one job per half day), a day takes as many
      // measurements as fit — offer starts on the hour.
      slotSpacingMinutes: 60,
      notificationEmails: ['jani.issakainen@markku.fi'],
      defaultFormValue: {
        ventilationType: 'mechanical_exhaust',
        floors: 1,
        ventHeight: 'standard',
        filterChange: false,
      } satisfies IvMittausFormValue as Record<string, unknown>,
      getBookingData: getIvMittausBookingData,
      inferFormValue: inferIvMittausFormValue,
    },
  }

/**
 * Prefill for a booking link's query string — the website calculator handing
 * its answers to the booking page (`?ventilationType=heat_recovery&floors=2`).
 * Only keys the preset's schema knows are read; booleans and numbers are
 * coerced from their string form. A query that doesn't validate falls back to
 * the default in full, so a stale or hand-edited link can't produce an
 * unbookable form.
 */
export function presetFormValueFromQuery(
  presetKey: ServicePresetKey,
  query: Record<string, string>,
): Record<string, unknown> {
  const { defaultFormValue } = SERVICE_PRESET_DATA[presetKey]
  const schema: z.AnyZodObject = PRESET_FORM_VALUE_SCHEMAS[presetKey]
  const overrides = Object.fromEntries(
    Object.keys(schema.shape)
      .filter((key) => key in query)
      .map((key) => [key, coerceQueryValue(query[key])]),
  )
  if (!Object.keys(overrides).length) return defaultFormValue

  const parsed = schema.safeParse({ ...defaultFormValue, ...overrides })
  return parsed.success ? parsed.data : defaultFormValue
}

function coerceQueryValue(value: string): unknown {
  if (value === 'true') return true
  if (value === 'false') return false
  if (value !== '' && !Number.isNaN(Number(value))) return Number(value)
  return value
}

/** A past visit at a property, reduced to what config inference needs. */
export interface BookedVisitTasks {
  visitId: number
  startTime: string | null
  tasks: BookedTaskLine[]
}

export interface ResolvedServiceConfig {
  formValue: Record<string, unknown>
  /** Visit the answers came from; null when the preset default was used. */
  source: { visitId: number; startTime: string | null } | null
}

/**
 * The config to offer for a repeat booking: answers from the property's most
 * recent visit this preset can read back, else the preset's default.
 *
 * `visits` must be newest-first — the caller's ORDER BY defines "most recent".
 */
export function resolveServiceConfig(
  presetKey: ServicePresetKey,
  visits: BookedVisitTasks[],
): ResolvedServiceConfig {
  const preset = SERVICE_PRESET_DATA[presetKey]

  for (const visit of visits) {
    const formValue = preset.inferFormValue?.(visit.tasks)
    if (formValue === UNREADABLE_SERVICE_CONFIG) break
    if (formValue) {
      return {
        formValue,
        source: { visitId: visit.visitId, startTime: visit.startTime },
      }
    }
  }

  return { formValue: preset.defaultFormValue, source: null }
}
